import { useMutation } from '@apollo/client'
import { type FetchInvitationsQuery, UpsertInvitationsDocument } from '@salescore/client-api'
import { getOrganizationIdFromPath, UserPlanTag } from '@salescore/client-common'
import { BUILTIN_USER_ROLES, type BuiltInUserRole } from '@salescore/features'
import { createDestroyColumn, getColumnSearchProps } from '@salescore/frontend-common'
import { message, Select, Table } from 'antd'
import { Order, String } from 'effect'
import { t } from 'i18next'
import { type ReactNode, useState } from 'react'

import { providerLabel, roleLabel, roleToRank } from '../users/UsersTable'
import { useCreateUserRoleOptions, USER_ROLE_JA } from './CreateInvitationForm'

type Invitation = FetchInvitationsQuery['invitations'][0]

// 権限カラムは UsersTable の既定ソートに合わせて権限の強さ（roleToRank）を第1キーに使う。
// 同 rank 内（例: 異なる custom 権限はいずれも rank=6）で同一ラベルが連続するよう、
// 第2キーに label を入れる
export const compareInvitationsByRole = Order.mapInput(
  Order.Tuple([Order.Number, Order.make<string>((a, b) => String.localeCompare(b)(a))]),
  (x: Pick<Invitation, 'role'>) => [roleToRank(x.role), roleLabel(x.role)] as const,
)

export const compareInvitationsByProvider = Order.mapInput(
  Order.make<string>((a, b) => String.localeCompare(b)(a)),
  (x: Pick<Invitation, 'provider'>) => providerLabel(x.provider),
)

export const InvitationsTable = ({
  afterDestroy,
  invitations,
}: {
  invitations: FetchInvitationsQuery['invitations']
  afterDestroy: () => void
}): ReactNode => {
  const options = useCreateUserRoleOptions()
  const [upsertInvitationsMutation] = useMutation(UpsertInvitationsDocument)
  const [updatingUserIds, setUpdatingUserIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const onDestroy = async (record: Invitation) => {
    await upsertInvitationsMutation({
      variables: {
        organizationId: getOrganizationIdFromPath(),
        invitations: [
          {
            id: record.id,
            email: record.email,
            provider: record.provider,
            role: record.role,
            deleted: true,
          },
        ],
      },
      onCompleted: () => {
        afterDestroy()
      },
    })
  }
  return (
    <>
      <Table
        rowKey="id"
        pagination={false}
        columns={[
          {
            title: t(`メールアドレス`),
            dataIndex: 'email',
            sorter: Order.mapInput(
              Order.make<string>((a, b) => String.localeCompare(b)(a)),
              (x: Invitation) => x.email,
            ),
            ...getColumnSearchProps((record: Invitation) => record.email),
          },
          {
            title: t(`ライセンス`),
            width: 100,
            // UserPlanTag が SYNC → VISUALIZE の順にタグを出すので、その順で比較する
            sorter: Order.mapInput(
              Order.Tuple([
                Order.make<string>((a, b) => String.localeCompare(b)(a)),
                Order.make<string>((a, b) => String.localeCompare(b)(a)),
              ]),
              (x: Invitation) => [x.planForSync, x.planForVis] as const,
            ),
            render(_, record) {
              return <UserPlanTag userOrInvitation={record} />
            },
          },
          {
            title: t(`権限`),
            width: 200,
            sorter: compareInvitationsByRole,
            render(_, invitation) {
              const { role } = invitation
              return (
                <Select
                  style={{ width: 200 }}
                  className="w-full"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                  value={{ label: USER_ROLE_JA[role as BuiltInUserRole], value: role }}
                  labelInValue
                  options={options.filter((x) => x.value !== BUILTIN_USER_ROLES.minimal)}
                  optionRender={(option) => <span className="whitespace-normal break-words">{option.label}</span>}
                  loading={updatingUserIds.includes(invitation.id)}
                  disabled={updatingUserIds.includes(invitation.id) || invitation.role === BUILTIN_USER_ROLES.minimal} // Minimalはadmin画面でのみ設定可能
                  onChange={(e: { label: string; value: string }) => {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                    const role = e.value as BuiltInUserRole
                    setUpdatingUserIds((old) => [...old, invitation.id])

                    void upsertInvitationsMutation({
                      variables: {
                        organizationId: getOrganizationIdFromPath(),
                        invitations: [
                          {
                            id: invitation.id,
                            email: invitation.email,
                            provider: invitation.provider,
                            role,
                          },
                        ],
                      },
                      onCompleted: () => {
                        // eslint-disable-next-line sonarjs/no-nested-functions
                        setUpdatingUserIds((old) => old.filter((x) => x !== invitation.id))
                        void message.success(t(`役割を更新しました`))
                      },
                    })
                  }}
                />
              )
            },
          },
          {
            title: t(`ログイン方法`),
            width: 200,
            sorter: compareInvitationsByProvider,
            render(_, record) {
              return <div className="">{providerLabel(record.provider)}</div>
            },
          },
          createDestroyColumn(loading, setLoading, onDestroy),
        ]}
        dataSource={invitations}
      />
    </>
  )
}
